'use client';

import {
  ApplicationHealthIssueRow,
  Badge,
  MetaDefault,
} from '@terra/ui-design-system';
import { CancelCircleIcon, CheckmarkCircle02Icon } from '@terra/ui-icons';
import { Box, HStack, VStack, token } from '@terra/ui-styled-system';
import { useTranslations } from 'next-intl';
import { useMemo } from 'react';
import { useTargetHealthIssues } from '../../hooks/dashboard/useTargetNotificationCenter';
import { usePermissions } from '../../hooks/usePermissions';
import { useTargetType } from '../../store/projectStore';
import { DashboardActionRow } from './DashboardActionRow';
import { DashboardCard } from './DashboardCard';
import type { HomeDestination } from './home-destination';
import {
  resolveHealthIssueDestination,
  toHealthIssues,
} from './notification-destinations';
import { useOpenHomeDestination } from './useOpenHomeDestination';

export type LocalHealthIssue = {
  key: string;
  title: string;
  description: string;
  destination: HomeDestination;
};

type TargetHealthCardProps = {
  applicationId: string;
  localIssues?: LocalHealthIssue[];
  healthyDetail?: string;
};

const NO_LOCAL_ISSUES: LocalHealthIssue[] = [];

export function TargetHealthCard({
  applicationId,
  localIssues = NO_LOCAL_ISSUES,
  healthyDetail,
}: TargetHealthCardProps) {
  const t = useTranslations('home.health');
  const targetType = useTargetType();
  const { canManageApplications } = usePermissions();
  const { data, isLoading } = useTargetHealthIssues({
    targetId: applicationId,
    targetType,
  });
  const { openDestination, openDestinationContextMenu } =
    useOpenHomeDestination({ applicationId });
  const healthIssues = useMemo(
    () => toHealthIssues(data?.healthIssues ?? []),
    [data?.healthIssues],
  );
  const issueCount = (data?.count ?? healthIssues.length) + localIssues.length;
  const isHealthy = !isLoading && issueCount === 0;

  return (
    <DashboardCard
      title={t('title')}
      trailingContent={
        isLoading ? null : isHealthy ? (
          <HStack gap={1} alignItems="center" minW={0}>
            <CheckmarkCircle02Icon
              size={16}
              color={token('colors.fg.neutral.success')}
            />
            <MetaDefault color="fg.neutral.muted" truncate>
              {healthyDetail
                ? t('healthyWithDetail', { detail: healthyDetail })
                : t('healthy')}
            </MetaDefault>
          </HStack>
        ) : (
          <Badge variant="outline" size="sm" colorPalette="red">
            {t('requiresAttention', { count: issueCount })}
          </Badge>
        )
      }
    >
      {issueCount > 0 ? (
        <Box mx={-4} mb={-4}>
          <VStack gap={0} alignItems="stretch">
            {localIssues.map((issue, index) => (
              <DashboardActionRow
                key={issue.key}
                icon={
                  <CancelCircleIcon
                    size={16}
                    color={token('colors.fg.neutral.error')}
                  />
                }
                title={issue.title}
                description={issue.description}
                showDivider={index > 0}
                onClick={() => openDestination(issue.destination)}
                onContextMenu={(event) =>
                  openDestinationContextMenu(event, issue.destination)
                }
              />
            ))}
            {healthIssues.map((issue, index) => (
              <Box
                key={issue.id}
                px={1}
                borderTopWidth={localIssues.length + index > 0 ? '1px' : 0}
                borderTopStyle="solid"
                borderTopColor="border.neutral.subtle"
              >
                <ApplicationHealthIssueRow
                  issue={issue}
                  canManageApplications={canManageApplications}
                  showBottomBorder={false}
                  onFixIssue={({ type }) => {
                    const destination = resolveHealthIssueDestination(type);

                    if (destination) {
                      openDestination(destination);
                    }
                  }}
                />
              </Box>
            ))}
          </VStack>
        </Box>
      ) : null}
    </DashboardCard>
  );
}
